import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation } from 'react-router';
import Modal, { ModalBody, ModalFooter } from '../modal/modal';
import { FormControlTextarea } from '../form/FormControlTextarea';
import { InlineSpinner } from '../presentational/spinner';
import { Alert } from '../alerts';
import { ModerationReportContextType } from '../../../common/moderationTypes';
import {
  ModerationCategory,
  ModerationSubReason,
  ICategoryDefinition,
  CATEGORY_DEFINITIONS,
  CATEGORY_ACCENT_COLORS,
  SUB_REASON_ICONS,
  categoryLabelKey,
  categoryDescriptionKey,
  subReasonLabelKey,
  subReasonDescriptionKey,
  isDescriptionRequired
} from '../../../common/moderationReasonsConfig';
import { useModerationReport, ISubmitModerationReportParams } from './useModerationReport';
import { useIsAuthenticated, useSiteConfig } from '../../scripts/selectors/global';
import { ComplexTooltip } from '../presentational/tooltip';
import {
  RECAPTCHA_ENTERPRISE_SITE_KEY,
  TENCENT_CAPTCHA_APP_ID,
  shouldUseTencentCaptcha
} from '../../../common/captchaConfig';

// Module-level state: persist reCAPTCHA across modal opens to avoid re-render crashes
let recaptchaScriptLoadedGlobal = false;
let tencentScriptLoadedGlobal = false;
let recaptchaWidgetIdGlobal: number | null = null;
let recaptchaContainerGlobal: HTMLDivElement | null = null;

function ReportContextInfo(props: {
  contextType: ModerationReportContextType;
  entityId: number;
  entityName?: string;
  sourceUrl?: string;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const pageUrl = location.pathname + location.search;
  return (
    <ComplexTooltip
      placement="top"
      tooltip={
        <div className="slds-text-body--small" style={{ lineHeight: 1.5, maxWidth: 320 }}>
          <div className="slds-m-bottom--xx-small" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {t('moderation:modal.context-info-heading')}
          </div>
          <div>
            <b>{t('moderation:modal.context-info-type')}:</b> {props.contextType}
          </div>
          <div>
            <b>{t('moderation:modal.context-info-entity-id')}:</b>{' '}
            {props.entityId || t('moderation:modal.context-info-none')}
          </div>
          {props.entityName && (
            <div>
              <b>{t('moderation:modal.context-info-entity')}:</b> {props.entityName}
            </div>
          )}
          {props.sourceUrl && (
            <div>
              <b>{t('moderation:modal.context-info-source')}:</b> {props.sourceUrl}
            </div>
          )}
          <div>
            <b>{t('moderation:modal.context-info-page')}:</b> {pageUrl}
          </div>
        </div>
      }
    >
      <i className="fa fa-info-circle slds-text-body--small text-muted" style={{ cursor: 'help' }} />
    </ComplexTooltip>
  );
}

function ReasonCard(props: {
  icon: string;
  label: string;
  description: string;
  accentColor: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="moderation-report-modal__reason-card moderation-report-modal__reason-card--accented"
      style={{ '--moderation-accent-color': props.accentColor } as React.CSSProperties}
      onClick={props.onClick}
    >
      <div className="moderation-report-modal__reason-card-icon moderation-report-modal__reason-card-icon--tinted">
        <i className={`fa-solid ${props.icon}`} />
      </div>
      <div className="moderation-report-modal__reason-card-content">
        <div className="moderation-report-modal__reason-card-label">{props.label}</div>
        <div className="moderation-report-modal__reason-card-description">{props.description}</div>
      </div>
      <div className="moderation-report-modal__reason-card-arrow">
        <i className="fa-solid fa-chevron-right" />
      </div>
    </button>
  );
}

function SelectionSummary(props: { categoryDef: ICategoryDefinition; selectedSubReason?: ModerationSubReason | null }) {
  const { t } = useTranslation();
  const { categoryDef, selectedSubReason } = props;
  const accentColor = CATEGORY_ACCENT_COLORS[categoryDef.category];
  return (
    <div
      className="moderation-report-modal__selected-summary moderation-report-modal__selected-summary--accented"
      style={{ '--moderation-accent-color': accentColor } as React.CSSProperties}
    >
      <div className="moderation-report-modal__selected-summary-category">
        <i className={`fa-solid ${categoryDef.icon}`} />
        <span>{t(categoryLabelKey(categoryDef.category))}</span>
      </div>
      {selectedSubReason && (
        <>
          <div className="moderation-report-modal__selected-summary-separator">
            <i className="fa-solid fa-chevron-right" />
          </div>
          <div className="moderation-report-modal__selected-summary-sub-reason">
            {t(subReasonLabelKey(selectedSubReason))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Shared chrome for every report-modal screen: applies the spinner-proof overlay
 * class and the standard body/footer layout so each step only supplies its content.
 * Centralizing this keeps `overlayClassName` from being forgotten on a new screen
 * (the original z-index regression) and removes the per-screen `<Modal>` boilerplate.
 */
function ReportModalShell(props: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  footer: React.ReactNode;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      overlayClassName="moderation-report-modal__overlay"
      title={props.title}
    >
      <ModalBody>
        <div className={props.bodyClassName ?? 'moderation-report-modal__form'}>{props.children}</div>
      </ModalBody>
      <ModalFooter>{props.footer}</ModalFooter>
    </Modal>
  );
}

interface IModerationReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextType: ModerationReportContextType;
  entityId: number;
  entityName?: string;
  sourceUrl?: string;
}

type Step = 'category' | 'abuse-location' | 'abuse-redirect' | 'sub-reason' | 'details';

/**
 * Declarative back-navigation graph for the report wizard. Given the current step
 * (and the small bit of state the decision depends on) it returns the step to
 * return to, or null when the step is the entry point and has no Back action.
 * Kept pure and module-level so the transitions can be unit-tested in isolation.
 */
function resolveBackStep(
  step: Step,
  ctx: { selectedCategory: ModerationCategory | null; skipSubReason: boolean }
): Step | null {
  switch (step) {
    case 'category':
      return null;
    case 'abuse-location':
      return 'category';
    case 'abuse-redirect':
      return 'abuse-location';
    case 'sub-reason':
      return ctx.selectedCategory === ModerationCategory.ABUSE_HARASSMENT ? 'abuse-location' : 'category';
    case 'details':
      return ctx.skipSubReason ? 'category' : 'sub-reason';
  }
}

export function ModerationReportModal(props: IModerationReportModalProps) {
  const { isOpen, onClose, contextType, entityId, entityName, sourceUrl } = props;
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const siteConfig = useSiteConfig();
  const isCN = shouldUseTencentCaptcha(siteConfig?.country || '');

  const { submitReport, reset, setError, state, referenceCode, error, retryAfterSeconds } = useModerationReport();

  const [selectedCategory, setSelectedCategory] = useState<ModerationCategory | null>(null);
  const [selectedSubReason, setSelectedSubReason] = useState<ModerationSubReason | null>(null);
  const [description, setDescription] = useState('');
  const [step, setStep] = useState<Step>('category');
  const [captchaScriptLoaded, setCaptchaScriptLoaded] = useState(
    isCN ? tencentScriptLoadedGlobal : recaptchaScriptLoadedGlobal
  );

  const pendingSubmitParams = useRef<ISubmitModerationReportParams | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCategory(null);
      setSelectedSubReason(null);
      setDescription('');
      setStep('category');
      reset();
    }
  }, [isOpen, reset]);

  // Load captcha script once (persists across modal opens)
  useEffect(() => {
    if (isAuthenticated || !isOpen) {
      return;
    }

    if (isCN) {
      if (tencentScriptLoadedGlobal) {
        setCaptchaScriptLoaded(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://ca.turing.captcha.qcloud.com/TCaptcha-global.js';
      script.onload = () => {
        tencentScriptLoadedGlobal = true;
        setCaptchaScriptLoaded(true);
      };
      document.body.appendChild(script);
    } else {
      if (recaptchaScriptLoadedGlobal) {
        setCaptchaScriptLoaded(true);
        return;
      }
      (window as any).rioModerationCaptchaReady = () => {
        recaptchaScriptLoadedGlobal = true;
        setCaptchaScriptLoaded(true);
      };
      const script = document.createElement('script');
      script.src = 'https://www.recaptcha.net/recaptcha/enterprise.js?onload=rioModerationCaptchaReady&render=explicit';
      document.body.appendChild(script);
    }
    // No cleanup: scripts persist across modal opens to avoid re-render crashes
  }, [isOpen, isAuthenticated, isCN]);

  // Render invisible reCAPTCHA widget into a persistent DOM element (outside React's tree).
  // This avoids React's reconciliation unmounting/remounting the container and destroying the widget.
  useEffect(() => {
    if (!captchaScriptLoaded || isCN || isAuthenticated) {
      return;
    }

    const grecaptcha = (window as any).grecaptcha?.enterprise;
    if (!grecaptcha) {
      return;
    }

    // Widget already exists — just reset it
    if (recaptchaWidgetIdGlobal != null) {
      grecaptcha.reset(recaptchaWidgetIdGlobal);
      return;
    }

    // Create a persistent container outside React's DOM tree
    if (!recaptchaContainerGlobal) {
      recaptchaContainerGlobal = document.createElement('div');
      document.body.appendChild(recaptchaContainerGlobal);
    }

    recaptchaWidgetIdGlobal = grecaptcha.render(recaptchaContainerGlobal, {
      sitekey: RECAPTCHA_ENTERPRISE_SITE_KEY,
      size: 'invisible',
      callback: (token: string) => {
        const params = pendingSubmitParams.current;
        if (params) {
          pendingSubmitParams.current = null;
          submitReport({ ...params, recaptchaCode: token });
        }
      },
      'error-callback': () => {
        pendingSubmitParams.current = null;
        setError('captcha_invalid');
      },
      'expired-callback': () => {
        pendingSubmitParams.current = null;
        setError('captcha_invalid');
      }
    });
  }, [captchaScriptLoaded, isCN, isAuthenticated, submitReport, setError]);

  const selectedCategoryDef = selectedCategory
    ? CATEGORY_DEFINITIONS.find((d) => d.category === selectedCategory)
    : null;

  const handleSelectCategory = useCallback((category: ModerationCategory) => {
    setSelectedCategory(category);
    if (category === ModerationCategory.ABUSE_HARASSMENT) {
      // Disambiguate in-game vs site abuse before collecting a report.
      setSelectedSubReason(null);
      setStep('abuse-location');
      return;
    }
    const def = CATEGORY_DEFINITIONS.find((d) => d.category === category);
    if (def && def.subReasons.length === 1) {
      // Skip sub-reason step when only one option (e.g. Feature Request)
      setSelectedSubReason(def.subReasons[0]);
      setStep('details');
    } else {
      setSelectedSubReason(null);
      setStep('sub-reason');
    }
  }, []);

  const handleSelectAbuseLocation = useCallback((location: 'in-game' | 'site') => {
    if (location === 'in-game') {
      setStep('abuse-redirect');
    } else {
      setStep('sub-reason');
    }
  }, []);

  const handleSelectSubReason = useCallback((subReason: ModerationSubReason) => {
    setSelectedSubReason(subReason);
    setStep('details');
  }, []);

  const handleBack = useCallback(() => {
    reset();
    setDescription('');
    setSelectedSubReason(null);

    const skipSubReason = !!selectedCategoryDef && selectedCategoryDef.subReasons.length === 1;
    const target = resolveBackStep(step, { selectedCategory, skipSubReason });
    if (!target) {
      return;
    }
    // Returning to the category list clears the in-progress category selection.
    if (target === 'category') {
      setSelectedCategory(null);
    }
    setStep(target);
  }, [step, selectedCategoryDef, selectedCategory, reset]);

  const handleSubmit = useCallback(async () => {
    if (!selectedCategory || !selectedSubReason) {
      return;
    }

    if (isDescriptionRequired(selectedSubReason) && !description.trim()) {
      return;
    }

    const baseParams: ISubmitModerationReportParams = {
      contextType,
      contextEntityId: entityId,
      reason: selectedCategory,
      subReason: selectedSubReason,
      description: description.trim() || undefined,
      sourceUrl:
        sourceUrl || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined)
    };

    // Authenticated users skip captcha
    if (isAuthenticated) {
      await submitReport(baseParams);
      return;
    }

    // CN users: Tencent CAPTCHA popup
    if (isCN) {
      const { TencentCaptcha } = window as any;
      if (!TencentCaptcha) {
        setError('captcha_invalid');
        return;
      }

      try {
        const captcha = new TencentCaptcha(
          String(TENCENT_CAPTCHA_APP_ID),
          (res: any) => {
            if (res.ret === 0) {
              submitReport({
                ...baseParams,
                tencentTicket: res.ticket,
                tencentRandstr: res.randstr
              });
            }
          },
          { enableDarkMode: true }
        );
        captcha.show();
      } catch (_err) {
        setError('captcha_invalid');
      }
      return;
    }

    // Non-CN users: invisible reCAPTCHA Enterprise
    const grecaptcha = (window as any).grecaptcha?.enterprise;
    if (!grecaptcha || recaptchaWidgetIdGlobal == null) {
      setError('captcha_invalid');
      return;
    }
    pendingSubmitParams.current = baseParams;
    grecaptcha.execute(recaptchaWidgetIdGlobal);
  }, [
    selectedCategory,
    selectedSubReason,
    description,
    contextType,
    entityId,
    sourceUrl,
    submitReport,
    setError,
    isAuthenticated,
    isCN
  ]);

  const errorMessage = !error
    ? null
    : error === 'rate_limit'
      ? t('moderation:errors.rate-limit', { minutes: Math.ceil((retryAfterSeconds || 3600) / 60) })
      : t(`moderation:errors.${error.replace(/_/g, '-')}`, { defaultValue: t('moderation:errors.generic') });

  const title = entityName ? t('moderation:modal.title-with-name', { name: entityName }) : t('moderation:modal.title');

  const isSubmitDisabled =
    state === 'loading' ||
    !selectedCategory ||
    !selectedSubReason ||
    (isDescriptionRequired(selectedSubReason!) && !description.trim());

  const contextInfoProps = { contextType, entityId, entityName, sourceUrl };

  // Success is orthogonal to the step machine — it can be reached from the details step.
  if (state === 'success' && referenceCode) {
    return (
      <ReportModalShell
        isOpen={isOpen}
        onClose={onClose}
        title={t('moderation:success.title')}
        bodyClassName="moderation-report-modal__success"
        footer={
          <button type="button" className="slds-button slds-button--brand" onClick={onClose}>
            {t('moderation:success.close')}
          </button>
        }
      >
        <div className="moderation-report-modal__success-icon">
          <i className="fa fa-check-circle" />
        </div>
        <p className="moderation-report-modal__success-message">{t('moderation:success.message')}</p>
        <p className="moderation-report-modal__success-reference">
          <strong>{t('moderation:success.reference', { code: referenceCode })}</strong>
        </p>
        <p className="moderation-report-modal__success-note">{t('moderation:success.reference-note')}</p>
      </ReportModalShell>
    );
  }

  // Every split footer pairs the context-info tooltip with a trailing action; only
  // the action differs between screens (Cancel / Back / Back+Submit).
  const splitFooter = (action: React.ReactNode) => (
    <div className="moderation-report-modal__footer--split">
      <ReportContextInfo {...contextInfoProps} />
      {action}
    </div>
  );

  const backFooter = splitFooter(
    <button type="button" className="slds-button slds-button--neutral" onClick={handleBack}>
      {t('moderation:modal.back')}
    </button>
  );

  // abuse-location and sub-reason can only render with a resolved category; if it is
  // somehow missing, fall back to the details view (preserves the original guard).
  const needsCategoryDef = step === 'abuse-location' || step === 'sub-reason';
  const view: Step = needsCategoryDef && !selectedCategoryDef ? 'details' : step;

  let shellTitle = title;
  let body: React.ReactNode = null;
  let footer: React.ReactNode = backFooter;

  switch (view) {
    // Screen 1: Category selection
    case 'category':
      body = (
        <div className="moderation-report-modal__reason-cards">
          {CATEGORY_DEFINITIONS.map((def) => (
            <ReasonCard
              key={def.category}
              icon={def.icon}
              label={t(categoryLabelKey(def.category))}
              description={t(categoryDescriptionKey(def.category))}
              accentColor={CATEGORY_ACCENT_COLORS[def.category]}
              onClick={() => handleSelectCategory(def.category)}
            />
          ))}
        </div>
      );
      footer = splitFooter(
        <button type="button" className="slds-button slds-button--neutral" onClick={onClose}>
          {t('moderation:modal.cancel')}
        </button>
      );
      break;

    // Screen 1.5a: Abuse location disambiguation (in-game vs on-site)
    case 'abuse-location': {
      const abuseAccent = CATEGORY_ACCENT_COLORS[ModerationCategory.ABUSE_HARASSMENT];
      body = (
        <>
          <SelectionSummary categoryDef={selectedCategoryDef!} />
          <p className="moderation-report-modal__subtitle">{t('moderation:modal.abuse-location-question')}</p>
          <div className="moderation-report-modal__reason-cards">
            <ReasonCard
              icon="fa-gamepad"
              label={t('moderation:modal.abuse-location-ingame-label')}
              description={t('moderation:modal.abuse-location-ingame-description')}
              accentColor={abuseAccent}
              onClick={() => handleSelectAbuseLocation('in-game')}
            />
            <ReasonCard
              icon="fa-globe"
              label={t('moderation:modal.abuse-location-site-label')}
              description={t('moderation:modal.abuse-location-site-description')}
              accentColor={abuseAccent}
              onClick={() => handleSelectAbuseLocation('site')}
            />
          </div>
        </>
      );
      break;
    }

    // Screen 1.5b: In-game abuse redirect to Blizzard
    case 'abuse-redirect':
      shellTitle = t('moderation:modal.abuse-redirect-title');
      body = (
        <Alert
          type="warning"
          size="large"
          className="moderation-report-modal__alert"
          message={<span className="text-bold">{t('moderation:modal.abuse-redirect-heading')}</span>}
          description={t('moderation:modal.abuse-redirect-message')}
        />
      );
      footer = (
        <div className="moderation-report-modal__redirect-footer">
          <button type="button" className="slds-button slds-button--neutral" onClick={handleBack}>
            {t('moderation:modal.back')}
          </button>
          <a
            href="https://support.blizzard.com"
            target="_blank"
            rel="noopener noreferrer"
            className="slds-button slds-button--brand"
          >
            {t('moderation:modal.abuse-redirect-cta')} <i className="fa fa-external-link slds-m-left--xx-small" />
          </a>
        </div>
      );
      break;

    // Screen 2: Sub-reason selection
    case 'sub-reason':
      body = (
        <>
          <SelectionSummary categoryDef={selectedCategoryDef!} />
          <p className="moderation-report-modal__subtitle">{t('moderation:modal.subtitle-select-sub-reason')}</p>
          <div className="moderation-report-modal__reason-cards">
            {selectedCategoryDef!.subReasons.map((subReason) => (
              <ReasonCard
                key={subReason}
                icon={SUB_REASON_ICONS[subReason]}
                label={t(subReasonLabelKey(subReason))}
                description={t(subReasonDescriptionKey(subReason))}
                accentColor={CATEGORY_ACCENT_COLORS[selectedCategoryDef!.category]}
                onClick={() => handleSelectSubReason(subReason)}
              />
            ))}
          </div>
        </>
      );
      break;

    // Screen 3: Details form
    case 'details': {
      const descriptionRequired = selectedSubReason ? isDescriptionRequired(selectedSubReason) : false;
      shellTitle = t('moderation:modal.title-details');
      body = (
        <>
          {selectedCategoryDef && selectedSubReason && (
            <SelectionSummary categoryDef={selectedCategoryDef} selectedSubReason={selectedSubReason} />
          )}

          <Alert
            type="info"
            className="slds-m-bottom--medium moderation-report-modal__alert"
            message={<span className="text-bold">{t('moderation:modal.support-ticket-heading')}</span>}
            description={
              <Trans
                i18nKey={
                  isAuthenticated
                    ? 'moderation:modal.support-ticket-disclaimer'
                    : 'moderation:modal.support-ticket-disclaimer-anon'
                }
              >
                <a
                  href="https://support.raider.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-bold moderation-report-modal__support-link"
                />
              </Trans>
            }
          />

          {selectedSubReason === ModerationSubReason.CHEATING_EXPLOIT && (
            <Alert
              type="info"
              className="slds-m-bottom--medium moderation-report-modal__alert"
              message={<span className="text-bold">{t('moderation:modal.cheating-blizzard-heading')}</span>}
              description={
                <Trans i18nKey="moderation:modal.cheating-blizzard-message">
                  <a
                    href="https://support.blizzard.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-bold moderation-report-modal__support-link"
                  />
                </Trans>
              }
            />
          )}

          {/* Description textarea */}
          <div className="moderation-report-modal__field">
            <label className="moderation-report-modal__label">
              {t('moderation:modal.description-label')}
              {descriptionRequired && <span className="moderation-report-modal__required"> *</span>}
            </label>
            <FormControlTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('moderation:modal.description-placeholder')}
              rows={4}
              maxLength={2000}
              variant="rio"
            />
            <p
              className="moderation-report-modal__hint"
              style={{ visibility: descriptionRequired && !description.trim() ? 'visible' : 'hidden' }}
            >
              {t('moderation:modal.description-required')}
            </p>
          </div>

          {/* Error message */}
          {state === 'error' && errorMessage && (
            <div className="moderation-report-modal__error">
              <i className="fa fa-exclamation-triangle" />
              <span>{errorMessage}</span>
            </div>
          )}
        </>
      );
      footer = splitFooter(
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="slds-button slds-button--neutral" onClick={handleBack}>
            {t('moderation:modal.back')}
          </button>
          <button
            type="button"
            className="slds-button slds-button--brand"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {state === 'loading' ? (
              <InlineSpinner className="moderation-report-modal__spinner" />
            ) : (
              t('moderation:modal.submit')
            )}
          </button>
        </div>
      );
      break;
    }
  }

  return (
    <ReportModalShell isOpen={isOpen} onClose={onClose} title={shellTitle} footer={footer}>
      {body}
    </ReportModalShell>
  );
}
